import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import { renderMarkdown } from '@/lib/markdown';
import { T, useTranslatedText } from '@/hooks/useAutoTranslate';

interface Article {
  id: number;
  category_id: number;
  title_zh: string;
  title_en: string;
  title_ru: string;
  summary_zh: string;
  content_zh: string;
  cover_img: string;
  tags: string[];
  read_time: string;
  published_at: string;
  updated_at: string;
  related_ids: number[];
  article_categories: { name_zh: string } | null;
}

interface ArticleStub {
  id: number;
  title_zh: string;
  cover_img: string;
  tags?: string[];
  published_at?: string;
  article_categories: { name_zh: string } | null;
}

const catColorMap: Record<string, string> = {
  '医学知识': 'bg-[#1a3a5c]/8 text-[#1a3a5c]',
  '法律政策': 'bg-amber-50 text-amber-700',
  '干货科普': 'bg-green-50 text-green-700',
};

function truncateTitle(title: string, maxLen: number = 26): string {
  if (!title) return '';
  if (title.length <= maxLen) return title;
  return title.slice(0, maxLen) + '...';
}

function ArticleContent({ content }: { content: string }) {
  const translatedContent = useTranslatedText(content || '');
  return (
    <div
      className="prose-sm text-gray-600 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(translatedContent) }}
    />
  );
}

export default function KnowledgeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [article, setArticle] = useState<Article | null>(null);
  const [related, setRelated] = useState<ArticleStub[]>([]);
  const [moreArticles, setMoreArticles] = useState<ArticleStub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');

    const load = async () => {
      try {
        const { data: art, error: artErr } = await supabase
          .from('articles')
          .select('id, category_id, title_zh, title_en, title_ru, summary_zh, content_zh, cover_img, tags, read_time, published_at, updated_at, related_ids, article_categories(name_zh)')
          .eq('id', id)
          .eq('status', 'published')
          .lte('published_at', new Date().toISOString())
          .maybeSingle();
        if (artErr) throw artErr;
        const articleData = art as Article | null;
        setArticle(articleData);

        if (!articleData) {
          setRelated([]);
          setMoreArticles([]);
          setLoading(false);
          return;
        }

        const tags = Array.isArray(articleData.tags) ? articleData.tags : [];
        const relatedIds = (articleData.related_ids || []).filter(
          (rid: unknown) => typeof rid === 'number'
        );

        // 一次查询所有已发布文章（排除自身），用于手动关联 + 标签自动匹配 + 更多文章
        const { data: allData } = await supabase
          .from('articles')
          .select('id, title_zh, cover_img, tags, published_at, article_categories(name_zh)')
          .eq('status', 'published')
          .lte('published_at', new Date().toISOString())
          .neq('id', id)
          .limit(200);

        const all = (allData ?? []) as ArticleStub[];

        // 1) 手动选择的关联文章（按后台设置顺序排列）
        const manualList = relatedIds
          .map((rid) => all.find((a) => a.id === rid))
          .filter((a): a is ArticleStub => Boolean(a));

        // 2) 不足3篇时，按标签重叠数 + 最新发布自动补齐
        let relatedList = [...manualList];
        if (relatedList.length < 3) {
          const usedIds = new Set<number>(relatedList.map((a) => a.id));
          const scored = all
            .filter((a) => !usedIds.has(a.id))
            .map((a) => {
              const aTags = Array.isArray(a.tags) ? a.tags : [];
              return { ...a, overlap: aTags.filter((t) => tags.includes(t)).length };
            })
            .sort(
              (a, b) =>
                b.overlap - a.overlap ||
                new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime(),
            );
          const need = 3 - relatedList.length;
          relatedList = [...relatedList, ...scored.slice(0, need)];
        }

        setRelated(relatedList.slice(0, 3));

        // 3) 更多文章：排除已显示在关联推荐里的
        const relatedUsedIds = new Set(relatedList.map((a) => a.id));
        const moreList = all
          .filter((a) => !relatedUsedIds.has(a.id))
          .sort(
            (a, b) =>
              new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime(),
          );
        setMoreArticles(moreList.slice(0, 4));
      } catch (err: any) {
        setError(err?.message || '加载失败，请重试');
        setRelated([]);
        setMoreArticles([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  // Auto-translate SEO fields (must be before early returns — React hooks rule)
  const translatedTitle = useTranslatedText(article?.title_zh || '');
  const translatedSummary = useTranslatedText(article?.summary_zh || (article?.content_zh || '').slice(0, 160).replace(/#+\s/g, '').replace(/\*\*/g, ''));
  const translatedShareTitle = encodeURIComponent(translatedTitle);

  if (loading) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center bg-[#f8f5f0]">
        <div className="text-center text-gray-400">
          <i className="ri-loader-4-line text-3xl animate-spin block mb-3"></i>
          <p className="text-sm">{t('knowledge_loading')}</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-3xl text-red-300 mb-3 block"></i>
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="text-[#c9a96e] text-sm cursor-pointer">{t('retry')}</button>
        </div>
      </main>
    );
  }

  if (!article) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">{t('knowledge_not_found')}</p>
          <Link to="/news" className="text-[#c9a96e] text-sm cursor-pointer">{t('knowledge_back')}</Link>
        </div>
      </main>
    );
  }

  const catName = article.article_categories?.name_zh || '';
  const tags = Array.isArray(article.tags) ? article.tags : [];
  const shareUrl = encodeURIComponent(window.location.href);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: translatedTitle || article.title_zh,
    description: translatedSummary || article.summary_zh || (article.content_zh || '').slice(0, 160).replace(/#+\s/g, '').replace(/\*\*/g, ''),
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    url: `${SITE_URL}/news/${article.id}`,
    image: article.cover_img,
    author: { '@type': 'Organization', name: 'RTB吉尔吉斯斯坦试管代孕中心' },
    publisher: { '@type': 'Organization', name: 'RTB吉尔吉斯斯坦试管代孕中心', url: SITE_URL },
    articleSection: catName,
    keywords: tags.join(', '),
  };

  return (
    <>
      <SeoHead
        title={`${translatedTitle || article.title_zh}｜RTB吉尔吉斯斯坦试管代孕中心`}
        description={translatedSummary || article.summary_zh || (article.content_zh || '').slice(0, 160).replace(/#+\s/g, '').replace(/\*\*/g, '')}
        keywords={tags.join(',')}
        canonical={`/news/${article.id}`}
        jsonLd={articleJsonLd}
      />
      <main className="pt-16 md:pt-20 bg-[#f8f5f0]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main content */}
            <article className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 md:p-8">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-xs text-gray-400 mb-5 flex-wrap">
                  <Link to="/" className="hover:text-[#c9a96e] cursor-pointer">{t('home')}</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <Link to="/news" className="hover:text-[#c9a96e] cursor-pointer">{t('knowledge_title')}</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-gray-600 line-clamp-1"><T text={article.title_zh} /></span>
                </div>

                <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full mb-4 ${catColorMap[catName] || 'bg-gray-100 text-gray-500'}`}>{catName}</span>

                <h1 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-4 leading-tight"><T text={article.title_zh} /></h1>

                <div className="flex items-center gap-4 text-gray-400 text-xs mb-6 pb-6 border-b border-gray-100">
                  <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{article.published_at ? article.published_at.slice(0, 10) : ''}</span>
                  {article.updated_at && article.updated_at !== article.published_at && (
                    <span className="flex items-center gap-1"><i className="ri-refresh-line"></i>更新于 {article.updated_at.slice(0, 10)}</span>
                  )}
                </div>

                {/* Article content */}
                <ArticleContent content={article.content_zh || ''} />

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-gray-100">
                  {tags.map((tag) => (
                    <Link key={tag} to={`/tags/${tag}`} className="text-xs bg-[#f8f5f0] text-gray-500 px-3 py-1 rounded-full hover:bg-[#f0e8d8] hover:text-[#c9a96e] transition-colors cursor-pointer">#{tag}</Link>
                  ))}
                </div>

                {/* Share */}
                <div className="mt-6 flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-gray-400">{t('knowledge_share')}</span>
                  <a href={`https://t.me/share/url?url=${shareUrl}&text=${translatedShareTitle}`} target="_blank" rel="nofollow noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0088cc] text-white cursor-pointer hover:opacity-90"><i className="ri-telegram-line text-sm"></i></a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="nofollow noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1877f2] text-white cursor-pointer hover:opacity-90"><i className="ri-facebook-line text-sm"></i></a>
                  <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${translatedShareTitle}`} target="_blank" rel="nofollow noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1da1f2] text-white cursor-pointer hover:opacity-90"><i className="ri-twitter-x-line text-sm"></i></a>
                  <a href={`https://wa.me/?text=${translatedShareTitle}%20${shareUrl}`} target="_blank" rel="nofollow noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-[#25d366] text-white cursor-pointer hover:opacity-90"><i className="ri-whatsapp-line text-sm"></i></a>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-6">
              {/* Related */}
              {related.length > 0 && (
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="font-bold text-[#1a3a5c] text-sm mb-4">{t('knowledge_related_title')}</h3>
                  <div className="space-y-4">
                    {related.map((r) => (
                      <Link to={`/news/${r.id}`} key={r.id} className="flex gap-3 group cursor-pointer">
                        <div className="w-16 h-12 rounded-lg overflow-hidden shrink-0">
                          <img src={r.cover_img} alt={r.title_zh} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#1a3a5c] font-medium group-hover:text-[#c9a96e] transition-colors leading-snug" title={r.title_zh}>
                            <T text={truncateTitle(r.title_zh, 26)} />
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* More articles */}
              {moreArticles.length > 0 && (
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="font-bold text-[#1a3a5c] text-sm mb-4">{t('knowledge_more_title')}</h3>
                  <div className="space-y-3">
                    {moreArticles.map((r) => (
                      <Link to={`/news/${r.id}`} key={r.id} className="flex items-start gap-2 group cursor-pointer">
                        <i className="ri-article-line text-[#c9a96e] text-xs mt-0.5 shrink-0"></i>
                        <p className="text-xs text-gray-500 font-medium group-hover:text-[#c9a96e] transition-colors leading-snug" title={r.title_zh}>
                          <T text={truncateTitle(r.title_zh, 30)} />
                        </p>
                      </Link>
                    ))}
                  </div>
                  <Link to="/news" className="mt-4 flex items-center gap-1 text-xs text-[#c9a96e] hover:underline cursor-pointer">
                    {t('knowledge_view_all')} <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}

              {/* CTA card */}
              <div className="bg-[#1a2f4a] rounded-2xl p-5 text-white">
                <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#c9a96e] mb-3">
                  <i className="ri-customer-service-line text-sm"></i>
                </div>
                <h3 className="font-bold text-sm mb-2">{t('knowledge_sidebar_cta')}</h3>
                <p className="text-white/60 text-xs leading-relaxed mb-4">{t('knowledge_sidebar_cta_desc')}</p>
                <Link to="/contact" className="block text-center bg-[#c9a96e] hover:bg-[#b8944f] text-white text-xs font-medium py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                  {t('nav_consult')}
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}